import React, {useMemo, useCallback} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {Link, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {categoryUrlBuilder} from '../../../../utils/url'
import useDeferredNavigation from '../../../../hooks/use-deferred-navigation'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

const TextLinkComponent = ({
    linkLabel,
    linkLabelTag,
    linkUrl,
    linkBackground,
    linkImageBackground,
    linkBorderColor,
    linkFontFamily,
    linkTextTransform,
    linkPaddingTopBottom,
    linkPaddingLeftRight,
    linkFontColor,
    linkUnderLineToggle,
    ctaType = 'button',
    textLinkAlignment,
    index,
    typeId,
    name
}) => {
    const pdTagManager = usePdTagManager()
    const appOrigin = useAppOrigin()
    const deferredNavigate = useDeferredNavigation()

    const resolvedFontColor = useMemo(() => linkFontColor || 'black', [linkFontColor])

    const isButton = ctaType === 'button'

    const redirectionUrl = useMemo(() => categoryUrlBuilder({c_alternativeUrl: linkUrl}), [linkUrl])

    const isGradientBg = useMemo(
        () =>
            isButton &&
            linkBackground &&
            typeof linkBackground === 'string' &&
            /^\s*linear-gradient\(/i.test(linkBackground),
        [isButton, linkBackground]
    )

    const isButtonWithBackground = useMemo(
        () => isButton && linkBackground,
        [isButton, linkBackground]
    )

    const isWhiteBg = useMemo(() => {
        if (!isButtonWithBackground || isGradientBg) return false
        const bg = linkBackground?.trim().toLowerCase()
        return bg === 'white' || bg === '#fff' || bg === '#ffffff'
    }, [isButtonWithBackground, isGradientBg, linkBackground])

    const hoverBgValue = useMemo(() => {
        if (!isButtonWithBackground || isGradientBg) return ''
        return isWhiteBg
            ? 'color-mix(in srgb, white 90%, black 10%)'
            : `color-mix(in srgb, ${linkBackground} 88%, white 12%)`
    }, [isButtonWithBackground, isGradientBg, isWhiteBg, linkBackground])

    const paddingStyles = useMemo(
        () => ({
            base: `${linkPaddingTopBottom || '10px'} ${linkPaddingLeftRight || '15px'}`,
            md: `${linkPaddingTopBottom || '10px'} ${linkPaddingLeftRight || '60px'}`
        }),
        [linkPaddingTopBottom, linkPaddingLeftRight]
    )

    const textSx = useMemo(
        () => ({
            ...(isButtonWithBackground && {
                '--btn-hover-mix': hoverBgValue,
                transition: isGradientBg ? 'opacity .15s ease' : 'background-color .15s ease',
                ...(isGradientBg
                    ? {_hover: {opacity: 0.9}}
                    : {_hover: {backgroundColor: 'var(--btn-hover-mix)'}})
            })
        }),
        [isButtonWithBackground, hoverBgValue, isGradientBg]
    )

    const handleClick = useCallback(
        (event) => {
            deferredNavigate(event, redirectionUrl, () => {
                if (index !== undefined && linkLabel) {
                    pdTagManager.selectComponent({
                        componentId: typeId,
                        componentName: name,
                        titleName: linkLabel,
                        titlePosition: index,
                        linkUrl:
                            redirectionUrl?.indexOf('.com') !== -1
                                ? redirectionUrl
                                : `${appOrigin}${redirectionUrl}`
                    })
                }
            })
        },
        [deferredNavigate, redirectionUrl, index, linkLabel, pdTagManager, typeId, name, appOrigin]
    )

    if (!linkUrl) {
        return null
    }

    return (
        <Link
            to={redirectionUrl}
            onClick={handleClick}
            as={RouteLink}
            display="flex"
            className="button-link"
            width="150px"
            overflow="hidden"
            flexShrink="0"
            backgroundImage={linkImageBackground && isButton ? linkImageBackground : 'unset'}
            backgroundSize="100% 75px"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            height={linkImageBackground && isButton ? '75px' : 'unset'}
            flexGrow={textLinkAlignment === 'left' ? 'unset' : '1'}
            _hover={{textDecoration: 'none'}}
            textDecoration={linkUnderLineToggle ? 'underline' : ''}
            textUnderlineOffset="4px"
        >
            {linkLabel && (
                <Text
                    as={linkLabelTag}
                    background={isButtonWithBackground ? linkBackground : 'transparent'}
                    fontSize="13px"
                    fontWeight="500"
                    letterSpacing="0.8px"
                    textStyle="h8"
                    color={resolvedFontColor}
                    border={isButton ? '1px solid ' : '0px solid'}
                    borderColor={linkBorderColor}
                    padding={paddingStyles}
                    fontFamily={linkFontFamily}
                    justifyContent="center"
                    display="flex"
                    alignItems="center"
                    textTransform={linkTextTransform}
                    flexGrow={textLinkAlignment === 'left' ? 'unset' : '1'}
                    width={{base: '150px', lg: textLinkAlignment === 'left' ? '150px' : 'unset'}}
                    css={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                    sx={textSx}
                >
                    {linkLabel}
                </Text>
            )}
        </Link>
    )
}

TextLinkComponent.propTypes = {
    linkLabel: PropTypes.string,
    linkLabelTag: PropTypes.string,
    linkUrl: PropTypes.string,
    linkBackground: PropTypes.string,
    linkImageBackground: PropTypes.string,
    linkBorderColor: PropTypes.string,
    linkFontFamily: PropTypes.string,
    linkFontColor: PropTypes.string,
    linkTextTransform: PropTypes.string,
    linkPaddingTopBottom: PropTypes.string,
    index: PropTypes.any,
    linkPaddingLeftRight: PropTypes.string,
    linkUnderLineToggle: PropTypes.bool,
    textLinkAlignment: PropTypes.string,
    ctaType: PropTypes.string,
    typeId: PropTypes.string,
    name: PropTypes.name
}

export const TextLink = React.memo(TextLinkComponent)
export default TextLink
